import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app } from '@/config/firebase';

export interface RestaurantProfile {
  id: number;
  client: number;
  name: string;
  description: string | null;
  cif: string;
  address: string;
  cp: string;
  poblacion: string;
  provincia: string;
  telefono: string;
  paginaweb: string | null;
  email: string;
  active: number;
  user_id: string;
  analytics: number;
  slug: string;
  municipio: string;
  pais: string;
  blocked: number;
  creation_date: string;
  last_update: string;
  business_unit_id: number;
  business_unit_name: string;
}

class RestaurantService {
  private db = getFirestore(app);
  private auth = getAuth(app);

  async getRestaurantProfile(userId: string): Promise<RestaurantProfile | null> {
    try {
      const docRef = doc(this.db, 'restaurants', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as RestaurantProfile;
      }
      return null;
    } catch (error) {
      console.error('Error fetching restaurant profile:', error);
      return null;
    }
  }

  async setRestaurantProfile(userId: string, profile: RestaurantProfile): Promise<void> {
    console.log('Setting restaurant profile for user:', userId);
    console.log('Profile data:', profile);
    
    if (!userId) {
      console.error('Cannot set restaurant profile: userId is empty');
      throw new Error('User ID is required');
    }
    
    // Check if user is authenticated
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user found');
      throw new Error('User must be authenticated to update profile');
    }
    
    if (currentUser.uid !== userId) {
      console.error('User ID mismatch:', { currentUserId: currentUser.uid, requestedUserId: userId });
      throw new Error('User ID mismatch');
    }
    
    // Check if user's token is still valid
    try {
      const token = await currentUser.getIdToken(false);
      console.log('User token retrieved successfully, length:', token.length);
    } catch (tokenError) {
      console.warn('Token may be expired, attempting refresh...');
      try {
        const refreshedToken = await currentUser.getIdToken(true);
        console.log('Token refreshed successfully, length:', refreshedToken.length);
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        throw new Error('Authentication token expired. Please log in again.');
      }
    }
    
    try {
      const docRef = doc(this.db, 'restaurants', userId);
      console.log('Document reference:', docRef.path);
      console.log('Attempting to save to Firestore...');
      
      await setDoc(docRef, profile, { merge: true });
      console.log('Restaurant profile saved successfully to Firestore');
    } catch (error: any) {
      console.error('Error setting restaurant profile:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', JSON.stringify(error, null, 2));
      
      // Provide more specific error messages for common Firestore errors
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your authentication status.');
      } else if (error.code === 'unavailable') {
        throw new Error('Firestore service is temporarily unavailable. Please try again.');
      } else if (error.code === 'unauthenticated') {
        throw new Error('Authentication required. Please log in again.');
      }
      
      throw error;
    }
  }
}

export const restaurantService = new RestaurantService();
